import React, { Fragment, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
    getAllPatients,
    getDoc,
    getDocIds,
    getPatientByLocalId,
    upsertPatients,
} from '../../store/idbHandler';
import Fuse from 'fuse.js';
import { NewPatient, Patient, getPtFullName } from '../../store/types/patient';
import AntAutoCompleteV2 from '../prescriptionPad/rightPanel/workspace/padV2/AntAutoComplete';
import { Radio, Row, notification } from 'antd';
import MergePtListItem from './MergePtListItem';
import ModalDialog from './ModalDialog';
import { dumpDynamicFormAutoSuggest } from '../../pages/utils';
import { getSearchResults, indexAllPatients } from '../../pages/allPatientsIndex';
import { track } from '../../tracking';
import upsertPatient, { PushPatientResponse } from '../../api/upsertPatient';
import archivePatient from '../../api/archivePatient';
import archivePatientInParchi from '../../api/archivePatientInParchi';

// Types vs Interfaces as React Prop Types => https://react-typescript-cheatsheet.netlify.app/docs/basic/getting-started/basic_type_example/#useful-table-for-types-vs-interfaces
interface Props {
    onClose: () => void;
    ptOid?: string;
    setPrimaryProf?: (val: Patient) => void;
    zIndex?: number;
}

const fuseConfig = {
    keys: ['_name', '_phone', '_uhid'],
    threshold: 0.1,
    ignoreLocation: true,
    useExtendedSearch: true,
    shouldSort: false,
};

const MergeProfileDialog: React.FC<Props> = (props) => {
    const [selectedOptions, setSelectedOptions] = React.useState<NewPatient[]>([]);
    const [primaryProfileId, setPrimaryProfileId] = useState('');
    const [selectedOption, setSelectedOption] = useState<NewPatient>();
    const { onClose, setPrimaryProf } = props;
    const [isLoading, setLoading] = useState<boolean>(false);
    const hasUserSelected = useRef(false);

    function onSelect(data?: NewPatient) {
        if (data) {
            const exists = selectedOptions.find((s) => s.id === data.id);

            if (!exists) {
                setSelectedOption(undefined);
                setSelectedOptions((selectedOptions) => [...selectedOptions, data]);

                if (hasUserSelected.current === false) {
                    if (data.personal.onApp && data.personal.onApp === true) {
                        setPrimaryProfileId(data.id);
                    }
                }
            }
        }
    }

    async function onSubmit() {
        setLoading(true);

        try {
            const primaryProfileObj = selectedOptions.find((s) => s.id === primaryProfileId);

            if (!primaryProfileObj) {
                throw new Error(`Cant find ${primaryProfileId} in selected options`);
            }

            const ptOidsToBeArchived = selectedOptions
                .filter((s) => s['id'] !== primaryProfileId)
                .map((val) => val.id);

            const promises = [
                upsertPatient({
                    ...primaryProfileObj,
                    link: [...(primaryProfileObj.link || []), ...ptOidsToBeArchived],
                }),
                ...ptOidsToBeArchived.map((ptId) =>
                    [archivePatient(ptId), archivePatientInParchi(ptId)].flat(),
                ),
                upsertPatients(
                    selectedOptions
                        ?.filter((s) => s['id'] !== primaryProfileId)
                        .map((pt) => ({ ...pt, archived: true })),
                ),
            ];

            // Only giving error in the local dev server
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            await Promise.all(promises);

            notification.success({
                message: `${ptOidsToBeArchived.length + 1} Patients merged successfully`,
                duration: 5,
            });

            if (setPrimaryProf) {
                setPrimaryProf(primaryProfileObj);
            }

            setLoading(false);
            onClose();
            track('MERGE_PATIENTS', {
                primaryProfileId,
                ptOidsToBeArchived: String(ptOidsToBeArchived),
            });
        } catch (e) {
            console.error(e);
            notification.error({
                message: 'Failed to Merge Patients',
                duration: 5,
            });
            setLoading(false);
        }
    }

    useEffect(() => {
        if (props.ptOid) {
            getPatientByLocalId(props.ptOid).then((currPt) => {
                if (!currPt) {
                    throw new Error(`Couldnt find pt: ${props.ptOid}`);
                }

                const newCurrPt = {
                    ...currPt,
                    _name: getPtFullName(currPt),
                    _phone: currPt.personal.phone?.n,
                    _uhid: currPt.formData?.find((f: any) => f.key === 'uhid')?.value,
                };

                setSelectedOption(newCurrPt as NewPatient);
                setSelectedOptions([newCurrPt] as NewPatient[]);
                setPrimaryProfileId(props.ptOid ?? '');
            });
        }
    }, []);

    async function onSearch(value: string): Promise<NewPatient[]> {
        const { patientsFuse } = await indexAllPatients();
        const results = getSearchResults(value, patientsFuse).filteredPatients;
        // console.log(results);
        return new Promise((res, _) =>
            res(
                results.filter((t) => {
                    const exists = selectedOptions.find((s) => s.id === t.id);
                    return !exists;
                }) as NewPatient[],
            ),
        );
    }

    return (
        <ModalDialog>
            <motion.div
                initial={{ opacity: 0 }}
                exit={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{ zIndex: 9999 }}
                transition={{ duration: 0.2, ease: 'linear' }}
                className="font-lato select-none"
            >
                <div className="modal-overlay" onClick={onClose}></div>
                <div className="modal-content flex flex-col max-h-592 h-4/5 font-lato font-400 rounded-8 w-50p">
                    <div className="flex items-center justify-between p-16 border-b bg-neutral-9 rounded-t-8 border-neutral-7">
                        <p className="font-700 text-16">Merge Patient Profiles</p>
                        <div
                            className="flex items-center justify-center w-16 h-16 cursor-pointer"
                            onClick={onClose}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 320 512"
                                className="w-20 h-20 fill-neutral-5 hover:fill-neutral-black"
                            >
                                <path d="M207.6 256l107.72-107.72c6.23-6.23 6.23-16.34 0-22.58l-25.03-25.03c-6.23-6.23-16.34-6.23-22.58 0L160 208.4 52.28 100.68c-6.23-6.23-16.34-6.23-22.58 0L4.68 125.7c-6.23 6.23-6.23 16.34 0 22.58L112.4 256 4.68 363.72c-6.23 6.23-6.23 16.34 0 22.58l25.03 25.03c6.23 6.23 16.34 6.23 22.58 0L160 303.6l107.72 107.72c6.23 6.23 16.34 6.23 22.58 0l25.03-25.03c6.23-6.23 6.23-16.34 0-22.58L207.6 256z"></path>
                            </svg>
                        </div>
                    </div>

                    <div className="overflow-scroll">
                        <div className="flex justify-center my-16 mx-26">
                            <AntAutoCompleteV2
                                autoFocus
                                selectedValue={selectedOption ? selectedOption : undefined}
                                onSearch={onSearch}
                                onSelect={onSelect}
                                getLabelToCompare={(option) => option.id}
                                placeholder="Search Patient by UHID / Phone / Name"
                                optionsRenderer={(option) => <MergePtListItem info={option} />}
                                clearAfterSelect={true}
                            />
                        </div>
                        <div className="selected-options">
                            {selectedOptions.length !== 0 && (
                                <Fragment>
                                    <div className="flex mb-16 mx-26">
                                        <p className="text-textColor-grey text-13">
                                            * Select One Profile as Primary
                                        </p>
                                    </div>
                                    <div className="mx-26">
                                        {selectedOptions.map((option) => (
                                            <Row className="my-10" key={option.id}>
                                                <Radio
                                                    // key={option['id']}
                                                    onChange={() => {
                                                        if (hasUserSelected.current !== true) {
                                                            hasUserSelected.current = true;
                                                        }
                                                        setPrimaryProfileId(option.id);
                                                    }}
                                                    checked={option.id === primaryProfileId}
                                                >
                                                    <MergePtListItem info={option} />
                                                </Radio>
                                            </Row>
                                        ))}
                                    </div>
                                </Fragment>
                            )}
                        </div>
                    </div>

                    <div className="flex bg-white mt-auto w-full items-center justify-between p-16 border-t border-neutral-7">
                        <div>
                            <button
                                onClick={onClose}
                                className="neutral-btn  px-12 py-8 btn--small "
                            >
                                <span className="w-full text-center">Cancel</span>
                            </button>
                        </div>
                        <div className="flex items-center space-x-16 select-none">
                            <button
                                disabled={
                                    isLoading
                                        ? true
                                        : selectedOptions.length > 1 && primaryProfileId !== ''
                                        ? false
                                        : true
                                }
                                onClick={onSubmit}
                                className="primary-btn  px-12 py-8 btn--small "
                            >
                                {isLoading && (
                                    <svg
                                        viewBox="0 0 512 512"
                                        className="fill-white animate-spin"
                                        width="20"
                                        height="20"
                                    >
                                        <path d="M456.433 371.72l-27.79-16.045c-7.192-4.152-10.052-13.136-6.487-20.636 25.82-54.328 23.566-118.602-6.768-171.03-30.265-52.529-84.802-86.621-144.76-91.424C262.35 71.922 256 64.953 256 56.649V24.56c0-9.31 7.916-16.609 17.204-15.96 81.795 5.717 156.412 51.902 197.611 123.408 41.301 71.385 43.99 159.096 8.042 232.792-4.082 8.369-14.361 11.575-22.424 6.92z" />
                                    </svg>
                                )}
                                <span className="w-full text-center">Submit</span>
                            </button>
                        </div>
                    </div>
                </div>
            </motion.div>
        </ModalDialog>
    );
};

export default MergeProfileDialog;
